import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";
import { scrollToWithStickyOffset } from "../utils/scrollToWithOffset";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function LineItemInput({
  value,
  onChange,
  onSelect,
  placeholder = "Description",
  className,
  disabled,
  "data-testid": testId,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimer = useRef(null);

  // Debounced search function
  const searchCatalog = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API}/catalog/search?q=${encodeURIComponent(query)}&limit=10`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.items || []);
      }
    } catch (error) {
      console.error("Error searching catalog:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle input change with debounce
  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setSelectedIndex(-1);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Debounce search (200ms)
    debounceTimer.current = setTimeout(() => {
      searchCatalog(newValue);
    }, 200);
  };

  // Handle selecting a suggestion
  const handleSelect = (item) => {
    onChange(item.description);
    if (onSelect) {
      onSelect({
        description: item.description,
        price: item.default_unit_price,
        is_taxable: item.default_is_taxable,
        catalog_item_id: item.item_id,
      });
    }
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Show suggestions when input is focused and there are suggestions
  useEffect(() => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [suggestions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && dropdownRef.current) {
      const selectedElement = dropdownRef.current.children[selectedIndex];
      if (selectedElement) {
        scrollToWithStickyOffset(selectedElement, { behavior: "auto", block: "nearest" });
      }
    }
  }, [selectedIndex]);

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) setShowSuggestions(true);
        }}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        data-testid={testId}
        autoComplete="off"
      />

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border-2 border-zinc-200 rounded-sm shadow-lg max-h-60 overflow-y-auto"
          data-testid="catalog-suggestions"
        >
          {suggestions.map((item, index) => (
            <div
              key={item.item_id}
              onClick={() => handleSelect(item)}
              className={cn(
                "px-3 py-2 cursor-pointer border-b border-zinc-100 last:border-b-0 transition-colors",
                selectedIndex === index
                  ? "bg-[#FF6600]/10"
                  : "hover:bg-zinc-50"
              )}
              data-testid={`catalog-suggestion-${index}`}
            >
              <div className="flex justify-between items-start gap-2">
                <span className="font-medium text-sm truncate flex-1">
                  {item.description}
                </span>
                <span className="text-sm text-zinc-500 whitespace-nowrap">
                  ${item.default_unit_price?.toFixed(2)}
                </span>
              </div>
              <div className="flex gap-2 mt-0.5">
                {item.default_is_taxable && (
                  <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 rounded">
                    Taxable
                  </span>
                )}
                {item.usage_count > 0 && (
                  <span className="text-xs text-zinc-400">
                    Used {item.usage_count}x
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Loading indicator */}
      {loading && value && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-zinc-300 border-t-[#FF6600] rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}
